
import { Component, Vue } from 'vue-property-decorator';
import Typed from 'typed.js';
import AuthRepository from '@/services/Auth';
import ProfileRepository from '@/services/Users-profile';

const TERMINAL_STRINGS = [
  'list websites --status ACTIVE',
  'get server origin-12 --services',
  'migrate group acme-corp --to eu-west',
  'dns records example.com --type A',
  'tail logs website/1042 --follow',
  'deploy staging shop.example.com',
];

interface PasswordQuizQuestion {
  prompt: string;
  options: string[];
}

const PASSWORD_QUIZ: PasswordQuizQuestion[] = [
  {
    prompt: 'How long is your password, emotionally?',
    options: [
      'Too short to admit',
      'Exactly 8 because policies said so',
      'A novel I typed once and never again',
      'I used a password manager and then closed the tab',
    ],
  },
  {
    prompt: 'Which sacred ingredient is definitely in it?',
    options: [
      'A pet’s name plus “123”',
      'My birth year, daringly rearranged',
      'qwerty with confidence',
      'Something I only remember at 3am',
    ],
  },
  {
    prompt: 'Where does the real copy of this password live?',
    options: [
      'A sticky note under the keyboard',
      'A Slack DM to myself from 2019',
      'In my head, which is currently buffering',
      'On a coworker’s whiteboard (don’t ask)',
    ],
  },
];

const QUIZ_REACTIONS = [
  'Interesting choice… the ritual nods respectfully.',
  'Noted. Absolutely not validated. Noted.',
  'Bold. Wrong in a spiritual sense. Proceed.',
  'The password remains unimpressed.',
];

@Component({
  inject: [
    'authRepository',
    'profileRepository',
  ],
})
export default class PagesLogin extends Vue {
  private readonly authRepository!: AuthRepository;
  private readonly profileRepository!: ProfileRepository;

  private typed: Typed | null = null;

  public readonly passwordQuiz = PASSWORD_QUIZ;

  public username = '';

  public password = '';

  public error = '';

  public loading = false;

  public showStaticCursor = false;

  public mode: 'login' | 'quiz' = 'login';

  /** 0 = intro, 1..N = questions, N+1 = result */
  public quizStep = 0;

  public lastReaction = '';

  public mounted(): void {
    const target = this.$refs.typedTarget as HTMLElement | undefined;
    if (!target) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      target.textContent = TERMINAL_STRINGS[0];
      this.showStaticCursor = true;
      return;
    }

    this.typed = new Typed(target, {
      strings: TERMINAL_STRINGS,
      typeSpeed: 38,
      backSpeed: 18,
      backDelay: 1600,
      startDelay: 400,
      loop: true,
      smartBackspace: true,
      showCursor: true,
      cursorChar: '█',
    });
  }

  public beforeDestroy(): void {
    if (this.typed) {
      this.typed.destroy();
      this.typed = null;
    }
  }

  public get currentQuestion(): PasswordQuizQuestion | null {
    if (this.quizStep < 1 || this.quizStep > this.passwordQuiz.length) {
      return null;
    }
    return this.passwordQuiz[this.quizStep - 1];
  }

  public onForgotPassword(): void {
    this.mode = 'quiz';
    this.quizStep = 0;
    this.lastReaction = '';
  }

  public startQuiz(): void {
    this.quizStep = 1;
    this.lastReaction = '';
  }

  public answerQuestion(_optionIndex: number): void {
    this.lastReaction = QUIZ_REACTIONS[this.quizStep % QUIZ_REACTIONS.length];
    this.quizStep += 1;
  }

  public exitQuiz(): void {
    this.mode = 'login';
    this.quizStep = 0;
    this.lastReaction = '';
  }

  public async login(): Promise<void> {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      const loginData = await this.authRepository.login(this.username, this.password);
      this.$store.commit('USER_LOGIN', loginData);

      const profile = await this.profileRepository.getOneByUserId(loginData.id);
      if (profile) {
        this.$store.commit('SET_PROFILE_DATA', profile);
      }

      this.$socket.io.opts.query = { key: loginData.jwt };
      this.$socket.connect();
      await this.$router.push({ name: 'home' });
      this.$vuetify.theme.dark = profile?.profileConfig?.enabledDarkTheme ?? true;
    } catch (err) {
      console.log(err);
      this.error = `${err.response?.data?.message}`;
    } finally {
      this.loading = false;
    }
  }
}
